/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {
  useCallback,
  useMemo
} from 'react';
import {
  useQuery,
  useQueryClient
} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getDeviceManufacturingInfoByPuls} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeviceManufacturingInfoResult = UseDefaultQueryResult & {
  manufacturingInfo: GenericObject;
  refresh: () => void;
};

/**
 * Retrieves manufacturing information of the device with the given identifier.
 * @param deviceIdentifier the identifier of the device (either the esn or serial number).
 */
const getDeviceManufacturingInfo = async(
  deviceIdentifier: string,
  useCMF = false
): Promise<GenericObject | null> => {
  try {
    const manufacturingInfo = await getDeviceManufacturingInfoByPuls(deviceIdentifier, useCMF);
    return manufacturingInfo;
  } catch (err) {
    if (err?.is404) {
      return null;
    } else {
      error(err);
      throw new Error(err);
    }
  }
};

/**
 * Retrieves the manufacturing information for the device of the given identifier.
 *
 * @param deviceIdentifier identifier of the device to retrieve information for (esn or serial number)
 * @param useCMF if the device requires use of the CMF endpoint (usually yes if MDT device)
 * @param enabled if the query is enabled / disabled
 * @returns a query result
 */
export const useDeviceManufacturingInfo = (
  deviceIdentifier: string,
  useCMF = false,
  enabled = true
): UseDeviceManufacturingInfoResult => {
  const queryClient = useQueryClient();
  const key         = useMemo(() => (
    createKey(QueryKey.DEVICE_MANUFACTURING_INFO, deviceIdentifier, useCMF)
  ), [deviceIdentifier]);
  const query       = useQuery(key, async() => {
    const results = await getDeviceManufacturingInfo(deviceIdentifier, useCMF);
    return results;
  }, {
    ...getStaleTime(5) as DurationResult,
    enabled
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    manufacturingInfo : query?.data || {},
    refresh
  }) as UseDeviceManufacturingInfoResult;
};

export default useDeviceManufacturingInfo;
